import { Menu as MenuPrimitive } from '@base-ui/react'
import type { ParseKeys } from 'i18next'
import { ChevronDown, RotateCcw } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Banner } from '@/components/feedback/Banner'
import { Button } from '@/components/ui/Button'
import { Field, Textarea } from '@/components/ui/Field'
import { FormSheet } from '@/components/ui/FormSheet'
import { Sheet } from '@/components/ui/Sheet'
import { StatusPill } from '@/components/ui/StatusPill'
import { useFormDraft } from '@/components/ui/useFormDraft'
import { useSession } from '@/features/auth/api/useSession'
import { useReopenCase, useTransitionCase } from '@/features/cases/api/useCase'
import { ApiError } from '@/lib/api'
import { CAP, can } from '@/lib/can'
import { cn } from '@/lib/cn'
import { describeProblem, fieldErrorKey, fieldErrors } from '@/lib/problems'
import { CASE_TONES } from '@/lib/statusTone'
import type { CaseDetail, CaseStatus } from '@/types/domain'

/* The lifecycle surface on the record (FR-25, FR-37, AD-24).
 *
 * All of it lives here rather than in CaseHeader, which stays a component that
 * renders facts and owns no mutation: the moment a header both draws a status
 * and changes it, every test of the header needs a query client and every
 * screen that wants the header inherits three writes it did not ask for.
 *
 * What the header gets instead is a node, the way it already takes the chat
 * badge — so this file owns the whole thing: which moves are offered, which of
 * them stop to ask first, and where a refusal is rendered.
 *
 * Four rules shape it, and none of them is decoration:
 *
 *   · They are offered as ONE control rather than as a row of buttons. Five
 *     "Перевести в «…»" buttons across the top of a record is five verbs
 *     competing with the record itself, and the row wrapped onto its own line
 *     on any screen narrower than a desk monitor. A trigger that names the
 *     question — change status — and a menu that answers it with the states
 *     themselves says the same thing in one control's worth of chrome.
 *
 *   · The offered moves are the SERVER's answer (`nextStatuses`), not a table
 *     re-derived here. The board keeps a local copy of the transition rules
 *     because a drag cannot wait for a round trip; a record screen can ask, and
 *     one copy of the rules beats two that drift apart. That list is SHAPE only
 *     — it is computed from the transition table and knows nothing about who is
 *     holding the mouse — so a move this caller may not make is offered anyway
 *     and refused at the write, with the 403 rendered below the control
 *     (AD-21). A control worth hiding outright is gated here instead, the way
 *     the reopen is.
 *
 *   · UX-DR-9 — the two side-effect moves stop and ask. Financially closing
 *     ends the money side of a case and cancelling is permanent, and neither is
 *     something to discover having done. The other moves fire on the press:
 *     they are reversible positions in a live shipment, and a confirmation on
 *     every one of them teaches people to dismiss confirmations.
 *
 *   · FR-25 — a financial close that is refused NAMES the balances holding it.
 *     The 409's detail carries them, and they are rendered beside the control
 *     that was pressed. Not a toast, which is gone before the reader has read
 *     the second name, and above all not a greyed-out button, which states the
 *     refusal without ever stating the reason.
 */

/** The moves that stop and ask, and the words that name what each one does.
 *
 *  One table, so "does this need confirming" and "what does the confirmation
 *  say" cannot disagree — a move added to one list and forgotten in the other
 *  would either ask with no sentence or act with no question.
 *
 *  It holds KEYS, never translated strings: this is a module constant evaluated
 *  once at import, and a string baked in here would freeze in whichever
 *  language was active at startup (the reason StatusPill's maps hold tones
 *  only). */
const CONFIRMED_MOVES = {
  'financially-closed': {
    body: 'cases.lifecycle.confirmFinancialBody',
    action: 'cases.lifecycle.confirmFinancialAction',
  },
  cancelled: {
    body: 'cases.lifecycle.confirmCancelBody',
    action: 'cases.lifecycle.confirmCancelAction',
  },
} as const satisfies Record<string, { body: ParseKeys; action: ParseKeys }>

type ConfirmedMove = keyof typeof CONFIRMED_MOVES

function needsConfirmation(status: CaseStatus): status is ConfirmedMove {
  return status in CONFIRMED_MOVES
}

/** The named balances out of a refused financial close (AC 4).
 *
 *  The server's detail is `"this case cannot be closed financially: "` followed
 *  by the balances joined with `"; "` (casefile/handlers.go). The lead-in is
 *  English prose written for a log, and it is NOT rendered — the heading above
 *  the list says the same thing in the reader's language, which is the whole
 *  argument of AD-22/AD-27. What IS rendered is everything after it: those are
 *  the balances themselves, named by the only module that can see them, and no
 *  catalogue of ours can invent them.
 *
 *  A detail with no lead-in is kept WHOLE rather than dropped. An unrecognised
 *  shape is still the only account anybody has of why the close refused, and
 *  showing an unexpected sentence beats showing an empty list under a heading
 *  that promises names.
 *
 *  Which is why it is THAT prefix that is stripped and not "everything before
 *  the first colon": a balance names a party, and a party's name may carry a
 *  colon of its own. Cutting at the first one would eat the name and call the
 *  remainder a balance. The split follows the same rule: the separator is the
 *  server's actual `"; "`, not a bare semicolon, so a party punctuating its own
 *  name does not get torn into two half-bullets. */
const BALANCE_LEAD_IN = 'this case cannot be closed financially: '

function balanceLines(detail: string | undefined): string[] {
  if (!detail) return []
  const list = detail.startsWith(BALANCE_LEAD_IN)
    ? detail.slice(BALANCE_LEAD_IN.length)
    : detail
  return list
    .split('; ')
    .map((line) => line.trim())
    .filter((line) => line !== '')
}

/** One row of the menu. A module constant rather than repeated three times:
 *  three copies of a highlight rule is two that stop matching. */
const MENU_ITEM = cn(
  'flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5',
  'text-text-body text-[13px] leading-none select-none',
  'outline-none data-highlighted:bg-surface-hover',
  'data-disabled:cursor-not-allowed data-disabled:opacity-55',
)

export function CaseLifecycleActions({ record }: { record: CaseDetail }) {
  const { t } = useTranslation()
  const { data: user } = useSession()

  const transition = useTransitionCase()
  const reopen = useReopenCase(record.id)

  /* The confirmation's target is held SEPARATELY from whether it is open, so
     the panel keeps its words through its exit transition instead of emptying
     itself on the way out — the same reason useFormDraft re-seeds on open
     rather than blanking on close. The initial value is never read: nothing
     renders it until a press has set it. */
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [confirmTarget, setConfirmTarget] = useState<ConfirmedMove>(
    'financially-closed',
  )
  const [reopenOpen, setReopenOpen] = useState(false)
  const [reason, setReason] = useFormDraft(
    reopenOpen ? record.id : null,
    () => '',
  )

  /* One move at a time. Two lifecycle writes in flight against one case would
     race in the server's favour and leave the screen reporting whichever
     answered last, which is not necessarily the one the case ended on. */
  const busy = transition.isPending || reopen.isPending
  const moves = record.nextStatuses ?? []

  /* An AFFORDANCE, never the gate (AD-21, and can.ts says so at length): the
     server re-checks `case.reopen` on the request itself and answers 403 to a
     client that decided otherwise. Hiding it here spares a Super-admin-only
     control from every desk that could only be refused. */
  const reopenable =
    record.status === 'financially-closed' && can(user, CAP.caseReopen)

  /* The refusal AC 4 is about, told apart from every other one by two facts
     together: the move that failed was the financial close, and the server
     answered 409 — which is the only status that carries balances. Anything
     else (a 403, a 404, a lost connection) is a generic refusal and goes
     through describeProblem like every other refusal in this feature. */
  const balances =
    transition.error instanceof ApiError &&
    transition.error.status === 409 &&
    transition.variables?.status === 'financially-closed'
      ? balanceLines(transition.error.problem.detail)
      : []
  const genericFailure = balances.length === 0 ? transition.error : null

  /* The reopen's own refusal, when the server put it on the field: a blank or
     over-long reason comes back as a 422 on `reason` (the code is the
     contract, the sentence is ours — AD-22). Anything else is record-level and
     goes above the form. */
  const reasonError = fieldErrors(reopen.error)['reason']?.[0]

  // Nothing offered and nothing to reopen is a terminal case, and a terminal
  // case gets no lifecycle chrome at all rather than an empty toolbar.
  if (moves.length === 0 && !reopenable) return null

  /* The offered moves split in two, and the split is presentation rather than
     policy: the server's list is rendered whole, in its own order, with the one
     move that cannot be undone pulled below a separator. Cancelling sits at the
     bottom of a menu for the reason it used to carry the danger tone in a row —
     a permanent act should not be adjacent to the ordinary ones. */
  const cancel = moves.find((status) => status === 'cancelled')
  const ordinary = moves.filter((status) => status !== 'cancelled')

  const startMove = (status: CaseStatus) => {
    // The previous refusal goes with the previous attempt: a balance list left
    // standing over a different move reads as if that move was refused too.
    transition.reset()
    if (needsConfirmation(status)) {
      setConfirmTarget(status)
      setConfirmOpen(true)
      return
    }
    transition.mutate({ caseId: record.id, status })
  }

  return (
    <div
      role="group"
      aria-label={t('cases.lifecycle.label')}
      className="flex min-w-0 flex-col items-start gap-1.5"
    >
      <MenuPrimitive.Root>
        <MenuPrimitive.Trigger
          render={
            <Button variant="secondary" size="sm" iconRight={ChevronDown} />
          }
          /* A bare `disabled` rather than a blockedReason: the cause is not a
             rule the reader can act on, it is a write already in flight, and
             the trigger's own label says so while it lasts. */
          disabled={busy}
          className="shrink-0"
        >
          {busy ? t('cases.lifecycle.moving') : t('cases.lifecycle.change')}
        </MenuPrimitive.Trigger>

        <MenuPrimitive.Portal>
          <MenuPrimitive.Positioner
            sideOffset={4}
            align="start"
            className="z-[60]"
          >
            <MenuPrimitive.Popup
              aria-label={t('cases.lifecycle.label')}
              className={cn(
                'bg-surface border-border-subtle rounded-md border',
                'shadow-[var(--shadow-overlay)] min-w-[212px] p-1.5',
                'origin-(--transform-origin)',
                'transition-[opacity,transform] duration-[var(--dur-fast)]',
                'ease-[var(--ease-standard)]',
                'data-starting-style:scale-[0.98] data-starting-style:opacity-0',
                'data-ending-style:scale-[0.98] data-ending-style:opacity-0',
              )}
            >
              {ordinary.map((status) => (
                <MenuPrimitive.Item
                  key={status}
                  onClick={() => startMove(status)}
                  className={MENU_ITEM}
                >
                  {/* The move is offered as the STATE it lands in, wearing the
                      pill the record wears once it is there — so the menu reads
                      as the row of positions this case can take next rather
                      than as five sentences all beginning "move to". */}
                  <StatusPill tone={CASE_TONES[status]}>
                    {t(`caseStatus.${status}`)}
                  </StatusPill>
                </MenuPrimitive.Item>
              ))}

              {/* Below the line: the moves that are not simply the next
                  position. Reopening undoes a close, and cancelling is
                  permanent — DESIGN.md's rule for what cannot be undone is a
                  text label and a danger tone, never an icon alone, and a
                  separator is what stops either being pressed on the way past
                  the ordinary ones. */}
              {cancel || reopenable ? (
                <MenuPrimitive.Separator className="bg-line-hair my-1.5 h-px" />
              ) : null}

              {reopenable ? (
                <MenuPrimitive.Item
                  onClick={() => {
                    reopen.reset()
                    setReopenOpen(true)
                  }}
                  className={MENU_ITEM}
                >
                  <RotateCcw size={14} className="text-text-muted" aria-hidden />
                  {t('cases.lifecycle.reopen')}
                </MenuPrimitive.Item>
              ) : null}

              {cancel ? (
                <MenuPrimitive.Item
                  onClick={() => startMove(cancel)}
                  className={cn(MENU_ITEM, 'text-negative')}
                >
                  {t('cases.lifecycle.moveTo', { status: t('caseStatus.cancelled') })}
                </MenuPrimitive.Item>
              ) : null}
            </MenuPrimitive.Popup>
          </MenuPrimitive.Positioner>
        </MenuPrimitive.Portal>
      </MenuPrimitive.Root>

      {/* AC 4's frontend half. The balances are listed by name under a
          translated heading, in place, under the control that was pressed —
          the server has already done the work of saying WHICH balance is
          unsettled, and losing that to a generic "could not close" would throw
          away the only part of the answer anybody can act on. */}
      {balances.length > 0 ? (
        <Banner
          tone="negative"
          title={t('cases.lifecycle.balancesTitle')}
          className="w-full"
        >
          <ul className="flex list-disc flex-col gap-0.5 pl-4">
            {balances.map((balance) => (
              <li key={balance}>{balance}</li>
            ))}
          </ul>
        </Banner>
      ) : null}

      {genericFailure ? (
        <Banner
          tone="negative"
          title={t(describeProblem(genericFailure).detailKey)}
          className="w-full"
        />
      ) : null}

      {/* UX-DR-9: the two side-effect moves are confirmed before they are sent,
          and the confirmation is dismissable without acting — which is what
          makes it a question rather than a speed bump. */}
      <Sheet
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title={t('cases.lifecycle.confirmTitle')}
        footer={
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              variant="secondary"
              size="lg"
              disabled={busy}
              onClick={() => setConfirmOpen(false)}
              className="sm:w-auto"
            >
              {t('common.actions.cancel')}
            </Button>
            <Button
              size="lg"
              variant={confirmTarget === 'cancelled' ? 'danger' : 'primary'}
              disabled={busy}
              onClick={() => {
                setConfirmOpen(false)
                transition.mutate({ caseId: record.id, status: confirmTarget })
              }}
              className="sm:w-auto"
            >
              {t(CONFIRMED_MOVES[confirmTarget].action)}
            </Button>
          </div>
        }
      >
        {/* Naming the case and naming the consequence, in that order. "Are you
            sure?" over an unnamed record is a question nobody can answer. */}
        <p className="text-text-body text-[13px] leading-snug">
          {t(CONFIRMED_MOVES[confirmTarget].body, { number: record.number })}
        </p>
      </Sheet>

      {/* Reopening (FR-37). A reason is REQUIRED before this will submit: the
          action's whole justification is that it is audited, and an audit entry
          reading "reopened, no reason given" is the weaker record the
          requirement exists to prevent. The refusal is reported in the sheet,
          which is where the reader is. */}
      <FormSheet
        open={reopenOpen}
        onClose={() => setReopenOpen(false)}
        title={t('cases.lifecycle.reopenTitle', { number: record.number })}
        submitLabel={t('cases.lifecycle.reopenSubmit')}
        pending={reopen.isPending}
        disabled={reason.trim() === ''}
        /* A field's own refusal sits beside the field and a record-level one
           above the form, never both at once (FormSheet says so): the server
           answers a reason it will not accept with a 422 on `reason`, and that
           belongs on the box the reader has to retype, not in a banner
           pointing vaguely at the sheet. */
        error={
          reasonError || !reopen.error
            ? undefined
            : t(describeProblem(reopen.error).detailKey)
        }
        onSubmit={() =>
          reopen.mutate(reason.trim(), {
            // Closed only once the server has agreed, so the sheet still holds
            // the typed reason if it refuses (UX-DR-18).
            onSuccess: () => setReopenOpen(false),
          })
        }
      >
        <p className="text-text-muted text-[12.5px] leading-snug font-light">
          {t('cases.lifecycle.reopenBody')}
        </p>
        <Field
          label={t('cases.lifecycle.reason')}
          hint={t('cases.lifecycle.reasonHint')}
          error={reasonError ? t(fieldErrorKey(reasonError)) : undefined}
          required
        >
          {(props) => (
            <Textarea
              {...props}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              rows={3}
            />
          )}
        </Field>
      </FormSheet>
    </div>
  )
}
